import { animateScroll } from 'react-scroll';
import { STAGE } from 'ENV_CONFIG';
import { lazy } from 'react';

const NBSP = '\u00a0';

/**
 * @description creates a lazy loading component from the named exported component from '~/routes'
 * @param component - should be the exact name of the named exported component from '~/routes' ex: 'AboutYou'
 * @return {React.LazyExoticComponent<* | T>}
 */
export const getLazyRoute = (component = '') =>
  lazy(() =>
    import('~/routes').then((module) => ({
      default: module[component],
    })),
  );

export const smoothScrollTo = (position) => {
  animateScroll.scrollTo(position);
};

export const scrollToAnchor = (anchor, options = { block: 'start' }) => {
  const el = document.getElementById(anchor);
  if (el) {
    el.scrollIntoView(options);
  }
};

const sortNodesByPositionTop = (a, b) => {
  const pageY = window.pageYOffset;
  return a.getBoundingClientRect().y + pageY - (b.getBoundingClientRect().y + pageY);
};

export const scrollToFirstErrorElement = (target = '[data-anchor]') => {
  const anchors = document.querySelectorAll(target);
  if (anchors[0]) {
    const sortedByPosition = [...anchors].sort(sortNodesByPositionTop);
    sortedByPosition[0].scrollIntoView({
      block: 'start',
      behavior: 'smooth',
    });
  }
};

export const doScrollToErrorMessage = (doScroll) => {
  if (doScroll) {
    scrollToFirstErrorElement('[aria-label="Error Message"]');
  }
};

export const isString = (val) => typeof val === 'string';
export const isBoolean = (val) => typeof val === 'boolean';

export const capitalize = (s) => s[0].toUpperCase() + s.slice(1);
export const toUpperCase = (s) => s.toUpperCase();
export const toNonBreakableString = (string) =>
  isString(string) ? string.split(' ').join(NBSP) : string;

export const replaceWithNonbreakableSpaces = (str) =>
  typeof str === 'string' ? str.split(' ').join(String.fromCharCode(160)) : str;

export const getDisplayName = (Component) => Component.displayName || Component.name || 'Component';

export const getRandomNumber = (from, to) => Math.floor(Math.random() * to) + from;

export const isEmpty = (object) =>
  !object || (typeof object === 'string' && !object.trim()) || Object.keys(object).length === 0;

export const isNotEmpty = (object) => !isEmpty(object);

export const formatPrice = (price, zeroPriceText = 'Free') =>
  price.substr(1) === '0' ? zeroPriceText : price;

export const replaceItem = (array, index, item) => [
  ...array.slice(0, index),
  {
    ...array[index],
    ...item,
  },
  ...array.slice(index + 1),
];

export const getDomainAddress = (stage) => {
  switch (stage) {
    case STAGE.LOCAL:
      return 'localhost';
    case STAGE.DEV:
      return 'stubs-appointment-booking-ui.appointments.flex.jl-digital';
    case STAGE.TEST:
      return 'integration.project4.com';
    case STAGE.PROD:
    default:
      return 'johnlewis.com';
  }
};

export const createUrl = (url, base) => new window.URL(url, base);

export const isReferrerToTheHomePage = (referrer) => createUrl(referrer).pathname === '/';

export const isReferrerToTheAppointments = (referrer) =>
  createUrl(referrer).pathname.startsWith('/appointments/');

export const isAddressInJohnLewisDomain = (address, domainAddress) =>
  !!address && address.indexOf(domainAddress) !== -1;

export const redirectedFromLoginPage = (domainAddress) =>
  !!document.referrer && document.referrer.indexOf(`${domainAddress}/sso`) !== -1;

export const formatDataTest = (term = '', tail) =>
  [term, tail].join(' ').trim().replace(/\s+/g, '_').toLowerCase();

export const stringToBoolean = (str) =>
  isString(str) ? str.toLowerCase() === 'true' || str === '1' : Boolean(str);

export const getSafety = (object, path, defaultValue) => {
  const pathArray = Array.isArray(path) ? path : path.split('.').filter((i) => i.length);
  if (!pathArray.length || !object) {
    return object === undefined ? defaultValue : object;
  }
  return getSafety(object[pathArray.shift()], pathArray, defaultValue);
};

export const getClientRectInDocument = (el) => {
  const { top, left, height, width } = el.getBoundingClientRect();

  const { body, documentElement: docEl } = document;

  const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
  const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

  const clientTop = docEl.clientTop || body.clientTop || 0;
  const clientLeft = docEl.clientLeft || body.clientLeft || 0;

  return {
    top: Math.round(top + scrollTop - clientTop),
    left: Math.round(left + scrollLeft - clientLeft),
    height,
    width,
  };
};

// https://www.freecodecamp.org/news/pipe-and-compose-in-javascript-5b04004ac937/
export const compose =
  (...fns) =>
  (Component) =>
    fns.reduceRight((PrevComponent, fn) => fn(PrevComponent), Component);
export const pipe =
  (...fns) =>
  (Component) =>
    fns.reduce((PrevComponent, fn) => fn(PrevComponent), Component);

export const addPrefixTo = (pathname) => `/${pathname}`;

export const debounce = (func, wait, immediate) => {
  let timeout;

  return (...args) => {
    const later = () => {
      timeout = null;
      if (!immediate) {
        func(...args);
      }
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) {
      func(...args);
    }
  };
};

export const throttle = (func, interval) => {
  let timeout;
  return (...args) => {
    const later = () => {
      timeout = false;
    };
    if (!timeout) {
      func(...args);
      timeout = true;
      setTimeout(later, interval);
    }
  };
};

export const sumOfPrices = (firstPrice, secondPrice) => {
  const priceSign = firstPrice.substr(0, 1);
  let sum = +firstPrice.substr(1);
  sum += +secondPrice.substr(1);
  sum = sum % 1 === 0 ? sum : sum.toFixed(2);
  return `${priceSign}${sum}`;
};

export const hasValuePredicate = (item) => Number.isInteger(item) || !!item;

export const formatPostcode = (postcode) => {
  const postcodeWithoutSpaces = postcode.replace(/\s/g, '');
  return postcodeWithoutSpaces.length >= 3
    ? `${postcodeWithoutSpaces.slice(
        0,
        postcodeWithoutSpaces.length - 3,
      )} ${postcodeWithoutSpaces.slice(postcodeWithoutSpaces.length - 3)}`
    : postcodeWithoutSpaces;
};
